import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  getOrderInitiationConfig,
  getQuoteData,
  getOrderInitiationScripInfo,
  orderInitiate,
  getStrategies,
} from "../../api/orderInitiation";
import {
  displayExpiryToApi,
  getApiFieldForScriptSelection,
  isScripDimensionApiField,
  formatScripValue,
  formatPctOfMarginRequired,
  parsePriceToNumber,
  scripInfoInputQuantity,
  orderQuantityFromQtyLotsField,
  mapOrderInitiationScripInfoResponse,
  buildOrderSlicePayloadFields,
  EMPTY_SCRIPT_SELECTION,
} from "./utils";
import { formatApiMessage } from "../../utils/formatApiMessage";
import { orderStatusNavigationStateFromResult } from "../../utils/normalizeOrderStatusResponse";
import { useDebouncedIncrement } from "../../hooks/useDebouncedIncrement";
import { useOrderInitiateScriptKeys } from "../../hooks/useOrderInitiateScriptKeys";
import {
  buildOrderInitiationConfigRequestList,
  mergeSelectionsWithDropdownDefaults,
  buildQuoteDataRequestListResolved,
} from "./selectionUtils";
import { VALIDITY_OPTIONS } from "./constants";
import TopMetricsRow from "./TopMetricsRow";
import ScriptListSection from "./ScriptListSection";
import CombinedSelectionSection from "./CombinedSelectionSection";
import StickyFooter from "./StickyFooter";
import { useBasketsAndClients, useClientRows } from "./hooks/useOrderInitiateData";
import styles from "./OrderInitiatePage.module.css";

function cloneQuoteDataSnapshot(q) {
  if (!q || typeof q !== "object") return null;
  try {
    return JSON.parse(JSON.stringify(q));
  } catch {
    return { ...q };
  }
}

function OrderInitiatePage() {
  const navigate = useNavigate();
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [scriptIds, setScriptIds] = useState([1]);
  const [selectedScriptIds, setSelectedScriptIds] = useState(() => new Set([1]));
  const [lastAddedScriptId, setLastAddedScriptId] = useState(null);
  const [scriptSelections, setScriptSelections] = useState(() => [{ ...EMPTY_SCRIPT_SELECTION }]);
  const [scriptDropdowns, setScriptDropdowns] = useState(null);
  const [quoteDataList, setQuoteDataList] = useState([]);
  const [scripInfo, setScripInfo] = useState(null);
  const [strategies, setStrategies] = useState([]);
  const lastAddedScriptRef = useRef(null);
  const clearAddedRef = useRef(null);
  const abortControllerRef = useRef(null);
  const quoteDataAbortRef = useRef(null);
  const scripInfoAbortRef = useRef(null);
  const orderInitiationConfigExchangeOnlyRef = useRef(false);
  const scriptSelectionsRef = useRef(scriptSelections);
  scriptSelectionsRef.current = scriptSelections;

  useEffect(() => {
    getStrategies()
      .then((data) => {
        if (Array.isArray(data)) {
          setStrategies(data);
        }
      })
      .catch((err) => {
        console.error("Failed to fetch strategies", err);
      });
  }, []);

  const strategyOptions = useMemo(
    () => strategies.map((s) => s.strategy_name),
    [strategies]
  );

  useEffect(() => {
    if (strategyOptions.length > 0) {
      setScriptSelections((prev) =>
        prev.map((sel) =>
          sel.strategy ? sel : { ...sel, strategy: strategyOptions[0] }
        )
      );
    }
  }, [strategyOptions]);

  const {
    baskets,
    selectedBasketIndices,
    hasBasketSelected,
    toggleBasket,
    filteredClients,
    clientsLoading,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
    updateClientRow,
    handleClientHeaderCheckboxChange,
    applyMarginToDeployToAllClients,
    applyMultiplierToAllClients,
  } = useBasketsAndClients();

  const marginRequiredFromMeta = useMemo(() => {
    const v = scripInfo?.MarginInfo?.InitialMarginRequired;
    if (v == null || v === "") return 0;
    const n = Number(String(v).replace(/,/g, "").trim());
    return Number.isNaN(n) ? 0 : n;
  }, [scripInfo]);

  const clientRows = useClientRows(
    filteredClients,
    marginRequiredFromMeta,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId
  );

  const allClientsChecked = clientRows.length > 0 && clientRows.every((r) => r.checked);
  const someClientsChecked = clientRows.some((r) => r.checked);

  const [clientSelectionPhase, setClientSelectionPhase] = useState("browse");
  const handleClientSelectionNext = useCallback(() => setClientSelectionPhase("selectedReview"), []);
  const handleClientSelectionBack = useCallback(() => setClientSelectionPhase("browse"), []);

  const {
    scriptSelectionsKey,
    orderInputKey,
  } = useOrderInitiateScriptKeys(scriptSelections);

  const debouncedApiTrigger = useDebouncedIncrement(scriptSelectionsKey, 650);
  /** Quote API must follow selection changes — separate debounce from config (650ms) so payload is not stale. */
  const debouncedQuoteTrigger = useDebouncedIncrement(scriptSelectionsKey, 300);
  const debouncedOrderInputTrigger = useDebouncedIncrement(orderInputKey, 500);

  useEffect(() => {
    const exchangeOnly = orderInitiationConfigExchangeOnlyRef.current;
    orderInitiationConfigExchangeOnlyRef.current = false;
    const requestList = buildOrderInitiationConfigRequestList(scriptSelectionsRef.current, exchangeOnly);

    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    getOrderInitiationConfig(requestList, { signal: abortControllerRef.current.signal })
      .then((data) => {
        setScriptDropdowns(data);
        const optsList = data?.quote_drop_down_options_list || [];
        if (optsList.length > 0) {
          setScriptSelections((prev) => mergeSelectionsWithDropdownDefaults(prev, optsList));
        }
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to load dropdown options", err);
      });
    return () => abortControllerRef.current?.abort();
  }, [debouncedApiTrigger]);

  useEffect(() => {
    let isSubscribed = true;
    let timeoutId;
    const fetchQuotes = async () => {
      if (!isSubscribed) return;
      // Wait for async get_quote_drop_down_options — without lists we cannot align payload to API options.
      if (!scriptDropdowns?.quote_drop_down_options_list?.length) {
        setQuoteDataList([]);
        return;
      }
      const requestList = buildQuoteDataRequestListResolved(
        scriptSelectionsRef.current,
        scriptDropdowns
      );
      if (requestList.length === 0) {
        setQuoteDataList([]);
        return;
      }
      const rowCount = requestList.length;
      if (quoteDataAbortRef.current) quoteDataAbortRef.current.abort();
      quoteDataAbortRef.current = new AbortController();
      try {
        const data = await getQuoteData(requestList, { signal: quoteDataAbortRef.current.signal });
        if (!isSubscribed) return;
        const quoteList = data?.quote_data_list ?? [];
        const padded = Array.from({ length: rowCount }, (_, i) => quoteList[i] ?? null);
        setQuoteDataList(padded);
      } catch (err) {
        if (err.name !== "AbortError") console.error("Failed to load quote data", err);
      }
      if (isSubscribed) timeoutId = setTimeout(fetchQuotes, 10000);
    };
    fetchQuotes();
    return () => {
      isSubscribed = false;
      if (timeoutId) clearTimeout(timeoutId);
      quoteDataAbortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuoteTrigger, scriptDropdowns]);

  useEffect(() => {
    const validQuotes = quoteDataList.filter((q) => q && q.tradingsymbol && q.instrument_token);
    if (validQuotes.length === 0) {
      setScripInfo(null);
      return;
    }
    if (scripInfoAbortRef.current) scripInfoAbortRef.current.abort();
    scripInfoAbortRef.current = new AbortController();
    const rows = scriptSelectionsRef.current;
    const requestScripList = validQuotes.map((q, i) => {
      const sel = rows[i] || {};
      return {
        trading_symbol: q.tradingsymbol,
        zerodha_instrument_token: String(q.instrument_token),
        order_price:
          sel.price && String(sel.price).trim() !== ""
            ? parsePriceToNumber(sel.price)
            : q.last_price != null
              ? Number(q.last_price)
              : 0,
        transaction_type: sel.transactionType ? sel.transactionType.toUpperCase() : "BUY",
        input_quantity: scripInfoInputQuantity(sel.qtyLots, sel.exchange),
        lot_size: q.lot_size != null ? String(q.lot_size) : "1",
        exchange: sel.exchange || "NFO",
        product_type: sel.productType ? sel.productType.toUpperCase() : "DELIVERY",
      };
    });
    const underlyingToken = validQuotes[0].underlying_instrument_token || 0;
    getOrderInitiationScripInfo(requestScripList, underlyingToken, {
      signal: scripInfoAbortRef.current.signal,
    })
      .then((data) => {
        const mapped = mapOrderInitiationScripInfoResponse(data);
        if (mapped) setScripInfo(mapped);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to fetch scrip info", err);
      });
    return () => {
      scripInfoAbortRef.current?.abort();
    };
  }, [quoteDataList, debouncedOrderInputTrigger]);

  const handleAddScrip = useCallback(() => {
    if (scriptIds.length >= 10) return;
    const first = scriptSelectionsRef.current[0];
    if (!first) return;
    const newSelection = { ...first };
    const newId = Date.now();
    setScriptIds((prev) => [...prev, newId]);
    setSelectedScriptIds((prev) => new Set([...prev, newId]));
    setScriptSelections((prev) => [...prev, newSelection]);
    setQuoteDataList((prev) => {
      const optimistic = cloneQuoteDataSnapshot(prev[0]);
      return [...prev, optimistic];
    });
    setLastAddedScriptId(newId);
  }, [scriptIds.length]);

  useEffect(() => {
    if (lastAddedScriptId == null) return;
    const scrollTimer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        lastAddedScriptRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    });
    clearAddedRef.current = setTimeout(() => setLastAddedScriptId(null), 500);
    return () => {
      cancelAnimationFrame(scrollTimer);
      if (clearAddedRef.current) clearTimeout(clearAddedRef.current);
    };
  }, [lastAddedScriptId]);

  const handleDeleteScrip = useCallback((id, index) => {
    setScriptIds((prev) => (prev.length <= 1 ? prev : prev.filter((sid) => sid !== id)));
    setSelectedScriptIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setScriptSelections((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
    setQuoteDataList((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }, []);

  const handleScriptSelectedChange = useCallback((scriptId, checked) => {
    setSelectedScriptIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(scriptId);
      else next.delete(scriptId);
      return next;
    });
  }, []);

  const handleScriptSelectionChange = useCallback((scriptIndex, field, value) => {
    const apiField = getApiFieldForScriptSelection(field);
    const apiValue = apiField === "expiry_date" ? displayExpiryToApi(value) : value;

    if (apiField === "exchange") {
      const prevRow = scriptSelectionsRef.current[scriptIndex];
      const prevEx = (prevRow?.exchange ?? "").toString().trim();
      const nextEx = (apiValue ?? "").toString().trim();
      if (prevEx !== nextEx) {
        orderInitiationConfigExchangeOnlyRef.current = true;
      }
    } else if (isScripDimensionApiField(apiField)) {
      orderInitiationConfigExchangeOnlyRef.current = false;
    }

    setScriptSelections((prev) =>
      prev.map((s, i) => {
        if (i !== scriptIndex) return s;
        if (apiField === "exchange") {
          const prevEx = (s.exchange ?? "").toString().trim();
          const nextEx = (apiValue ?? "").toString().trim();
          if (prevEx === nextEx) {
            return s;
          }
          return {
            ...s,
            exchange: apiValue,
            segment: "",
            symbol: "",
            option_type: "",
            strike_price: "",
            expiry_date: "",
          };
        }
        if (apiField === "segment") {
          return {
            ...s,
            segment: apiValue,
            symbol: "",
            option_type: "",
            strike_price: "",
            expiry_date: "",
          };
        }
        if (apiField === "symbol") {
          return {
            ...s,
            symbol: apiValue,
            option_type: "",
            strike_price: "",
            expiry_date: "",
          };
        }
        if (apiField === "sweeping" && apiValue === true) {
          return { ...s, sweeping: true, slicing: false };
        }
        if (apiField === "slicing" && apiValue === true) {
          return { ...s, slicing: true, sweeping: false };
        }
        return { ...s, [apiField]: apiValue };
      })
    );
  }, []);

  const orderTypeToApi = useCallback((orderType) => {
    if (!orderType) return "MARKET";
    const t = String(orderType).toUpperCase();
    if (t.includes("MARKET")) return "MARKET";
    if (t.includes("LIMIT")) return "LIMIT";
    if (t === "AMO") return "AMO";
    return "MARKET";
  }, []);

  const handlePlaceOrder = useCallback(async () => {
    const selectedScripCount = selectedScriptIds.size;
    const selectedBasketCount = selectedBasketIndices.size;
    const totalClientsCount = clientRows.filter((r) => r.checked).length;
    const clientGateOk =
      clientSelectionPhase === "selectedReview" || selectedBasketCount > 0;
    const canPlace =
      selectedScripCount > 0 && clientGateOk && totalClientsCount > 0;
    if (!canPlace) return;

    const selectedIndices = scriptIds
      .map((id, index) => (selectedScriptIds.has(id) ? index : null))
      .filter((i) => i !== null);
    const scripList = [];
    for (const index of selectedIndices) {
      const q = quoteDataList[index];
      const sel = scriptSelections[index] || {};
      const tradingSymbol = (q?.tradingsymbol) || sel.symbol || "";
      if (!tradingSymbol || !q?.instrument_token) continue;
      const lotSize = q.lot_size != null ? Number(q.lot_size) : 1;
      const orderQty = orderQuantityFromQtyLotsField(sel.qtyLots, sel.exchange, lotSize);
      const disclosedQty =
        sel.absDisQty && String(sel.absDisQty).trim() !== ""
          ? Number(sel.absDisQty)
          : orderQty;
      const orderPrice =
        sel.price && String(sel.price).trim() !== ""
          ? parsePriceToNumber(sel.price)
          : q.last_price != null
            ? Number(q.last_price)
            : 0;
      const strategyFromScrip = (sel.strategy && String(sel.strategy).trim()) || strategyOptions[0] || "";
      scripList.push({
        trading_symbol: tradingSymbol,
        zerodha_instrument_token: Number(q.instrument_token),
        order_quantity: orderQty,
        disclosed_quantity: disclosedQty,
        transaction_type: (sel.transactionType || "Buy").toUpperCase(),
        product_type: (sel.productType || "DELIVERY").toUpperCase(),
        order_type: orderTypeToApi(sel.orderType),
        order_price: orderPrice,
        trigger_price: 0,
        lot_size: lotSize,
        exchange: (sel.exchange || "NSE").toUpperCase(),
        strategy_name: strategyFromScrip || "",
        validity: (sel.validity || "DAY").toUpperCase(),
        ...buildOrderSlicePayloadFields(sel, orderQty),
      });
    }
    const client_multipliers = {};
    clientRows
      .filter((r) => r.checked)
      .forEach((row) => {
        const multStr =
          row.multiplier && String(row.multiplier).trim() !== ""
            ? row.multiplier
            : row.suggestedMultiplier;
        const mult = parseInt(String(multStr).trim(), 10);
        client_multipliers[row.clientCode] = Number.isNaN(mult) ? 0 : mult;
      });
    if (scripList.length === 0 || Object.keys(client_multipliers).length === 0) return;
    setIsPlacingOrder(true);
    try {
      const result = await orderInitiate(scripList, client_multipliers);
      navigate("/order-status", { state: orderStatusNavigationStateFromResult(result) });
    } catch (err) {
      console.error("Place order failed", err);
      const msg =
        formatApiMessage(err?.response?.data?.detail) ??
        formatApiMessage(err?.response?.data?.message) ??
        (typeof err?.message === "string" && err.message.trim() !== "" ? err.message : null) ??
        "Place order failed.";
      navigate("/order-status", { state: { squareOffError: msg } });
    } finally {
      setIsPlacingOrder(false);
    }
  }, [
    selectedScriptIds,
    selectedBasketIndices,
    clientRows,
    scriptIds,
    quoteDataList,
    scriptSelections,
    strategyOptions,
    orderTypeToApi,
    clientSelectionPhase,
    navigate,
  ]);

  const selectedScripCount = selectedScriptIds.size;
  const selectedBasketCount = selectedBasketIndices.size;
  const totalClientsCount = clientRows.filter((r) => r.checked).length;
  const clientGateOk =
    clientSelectionPhase === "selectedReview" || selectedBasketCount > 0;
  const canPlaceOrder =
    selectedScripCount > 0 && clientGateOk && totalClientsCount > 0;
  
  const { maxProfitVal, minProfitVal, maxProfitPct, maxLossPct, metricsGrid } = useMemo(() => {
    const marginForPct = scripInfo?.MarginInfo?.InitialMarginRequired;
    const maxProfitValInner = scripInfo ? formatScripValue(scripInfo.StrategyInfo?.MaxProfit) : "—";
    const minProfitValInner = scripInfo ? formatScripValue(scripInfo.StrategyInfo?.MaxLoss) : "—";
    const maxProfitPctInner = scripInfo
      ? formatPctOfMarginRequired(scripInfo.StrategyInfo?.MaxProfit, marginForPct)
      : "—";
    const maxLossPctInner = scripInfo
      ? formatPctOfMarginRequired(scripInfo.StrategyInfo?.MaxLoss, marginForPct)
      : "—";
    const grid = [
      {
        value: scripInfo?.StrategyInfo?.BreakevenList?.join(", ") || "—",
        label: "Breakeven",
      },
      {
        value: scripInfo ? formatScripValue(scripInfo.StrategyInfo?.TotalPremium) : "—",
        label: "Total Premium",
      },
      { value: scripInfo?.StrategyInfo?.RewardRisk || "—", label: "Reward/Risk" },
      {
        value: scripInfo ? formatScripValue(scripInfo.MarginInfo?.InitialMarginRequired) : "—",
        label: "Margin Required",
      },
      {
        value: scripInfo ? formatScripValue(scripInfo.StrategyInfo?.PremiumPerLot) : "—",
        label: "Premium/lot",
      },
      { value: "—", label: "Total Value" },
    ];
    return {
      maxProfitVal: maxProfitValInner,
      minProfitVal: minProfitValInner,
      maxProfitPct: maxProfitPctInner,
      maxLossPct: maxLossPctInner,
      metricsGrid: grid,
    };
  }, [scripInfo]);

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <TopMetricsRow
          maxProfitVal={maxProfitVal}
          minProfitVal={minProfitVal}
          maxProfitPct={maxProfitPct}
          maxLossPct={maxLossPct}
          metricsGrid={metricsGrid}
        />
      </div>

      <ScriptListSection
        scriptIds={scriptIds}
        lastAddedScriptId={lastAddedScriptId}
        lastAddedScriptRef={lastAddedScriptRef}
        scriptDropdowns={scriptDropdowns}
        scriptSelections={scriptSelections}
        quoteDataList={quoteDataList}
        selectedScriptIds={selectedScriptIds}
        strategyOptions={strategyOptions}
        validityOptions={VALIDITY_OPTIONS}
        onAddScrip={handleAddScrip}
        onDeleteScrip={handleDeleteScrip}
        onScriptSelectionChange={handleScriptSelectionChange}
        onScriptSelectedChange={handleScriptSelectedChange}
      />

      <CombinedSelectionSection
        baskets={baskets}
        selectedBasketIndices={selectedBasketIndices}
        toggleBasket={toggleBasket}
        hasBasketSelected={hasBasketSelected}
        basketClientsLoading={clientsLoading}
        clientRows={clientRows}
        updateClientRow={updateClientRow}
        applyMarginToDeployToAllClients={applyMarginToDeployToAllClients}
        applyMultiplierToAllClients={applyMultiplierToAllClients}
        allClientsChecked={allClientsChecked}
        someClientsChecked={someClientsChecked}
        onClientHeaderCheckboxChange={handleClientHeaderCheckboxChange}
        clientSelectionPhase={clientSelectionPhase}
        onClientSelectionNext={handleClientSelectionNext}
        onClientSelectionBack={handleClientSelectionBack}
      />

      <StickyFooter
        selectedScripCount={selectedScripCount}
        selectedBasketCount={selectedBasketCount}
        totalClientsCount={totalClientsCount}
        canPlaceOrder={canPlaceOrder}
        isPlacingOrder={isPlacingOrder}
        onPlaceOrder={handlePlaceOrder}
      />
    </div>
  );
}

export default OrderInitiatePage;
